import React, { useEffect, useState, useRef, useMemo } from 'react';
import {
  Loader2, AlertCircle, FileText, Calendar, Video,
  CheckCircle2, Search, Bot, MessageSquare, PlayCircle
} from 'lucide-react';

// --- HELPER: Advanced Transcript Parser (User Provided) ---
const parseTranscriptData = (rawEvents) => {
  // 1. Safety Unwrap
  let eventsToProcess = rawEvents;
  if (!Array.isArray(rawEvents)) {
    if (rawEvents && Array.isArray(rawEvents.events)) eventsToProcess = rawEvents.events;
    else return [];
  }

  const processedEvents = [];
  let lastKnownSpeaker = "Unknown";

  eventsToProcess.forEach((evt, evtIndex) => {
    // Handle different field names for text
    const rawText = evt.text || evt.content || evt.message || "";

    // A. Simple Case: Speaker is already defined in the object
    if (evt.speaker && evt.speaker !== 'Unknown' && !rawText.includes('\n')) {
      lastKnownSpeaker = evt.speaker;
      processedEvents.push({ ...evt, displaySpeaker: evt.speaker, displayText: rawText });
      return;
    }

    // B. Complex Case: Text contains newlines or embedded names
    const lines = rawText.split('\n');
    let currentBuffer = [];
    // Regex: Starts with Capital, allows letters/spaces/dots/dashes
    const nameRegex = /^[A-Z][a-zA-Z\s.-]+$/;

    lines.forEach((line, lineIndex) => {
      const trimmed = line.trim();
      if (!trimmed) return;

      // Detect if line is likely a Speaker Name (e.g. "John Doe")
      const isName = nameRegex.test(trimmed) && trimmed.length < 30 && trimmed.split(' ').length <= 5;

      if (isName) {
        // If we found a new name, push whatever buffer we had for the PREVIOUS speaker
        if (currentBuffer.length > 0) {
          processedEvents.push({
            ...evt,
            id: `${evt.id || 'evt'}_part_${evtIndex}_${lineIndex}`,
            displaySpeaker: lastKnownSpeaker,
            displayText: currentBuffer.join('\n'),
            speaker: lastKnownSpeaker,
            // approximate start time if needed
            start: evt.start || evt.start_ms || 0
          });
        }
        lastKnownSpeaker = trimmed;
        currentBuffer = [];
      } else {
        currentBuffer.push(line);
      }
    });

    // Flush remaining buffer
    if (currentBuffer.length > 0) {
      processedEvents.push({
        ...evt,
        id: `${evt.id || 'evt'}_final_${evtIndex}`,
        displaySpeaker: lastKnownSpeaker,
        displayText: currentBuffer.join('\n'),
        speaker: lastKnownSpeaker,
        start: evt.start || evt.start_ms || 0
      });
    }
  });

  return processedEvents;
};

// --- HELPER: Enhanced Markdown Summary Renderer ---
const formatContent = (markdownText) => {
  if (!markdownText) return <span className="italic opacity-50">No summary available.</span>;

  const lines = markdownText.split('\n');
  const elements = [];
  let currentList = [];
  let key = 0;

  const flushList = () => {
    if (currentList.length > 0) {
      elements.push(
        <ul key={`list-${key++}`} className="space-y-2 mb-6 ml-6 text-slate-700 dark:text-slate-300">
          {currentList.map((item, idx) => (
            <li key={idx} className="text-sm leading-relaxed pl-2 relative before:content-['•'] before:absolute before:left-[-1rem] before:text-orange-500 before:font-bold">
              {item}
            </li>
          ))}
        </ul>
      );
      currentList = [];
    }
  };

  lines.forEach((line, i) => {
    const trimmed = line.trim();

    // Skip empty lines
    if (!trimmed) {
      flushList();
      return;
    }

    // Handle headings: ## N. **Title** or ## Title
    const headingMatch = trimmed.match(/^#{1,6}\s+(\d+\.\s*)?(\*\*)?(.+?)(\*\*)?$/);
    if (headingMatch) {
      flushList();
      const number = headingMatch[1] || '';
      const titleText = headingMatch[3].replace(/\*\*/g, '');

      elements.push(
        <div key={`heading-${i}`} className="flex items-baseline gap-3 mb-4 mt-8 first:mt-0 pb-3 border-b border-gray-100 dark:border-white/10">
          {number && (
            <span className="text-2xl font-bold text-orange-500">
              {number.replace('.', '')}
            </span>
          )}
          <h3 className="text-lg font-bold text-slate-900 dark:text-white">
            {titleText}
          </h3>
        </div>
      );
      return;
    }

    // Handle list items
    if (trimmed.startsWith('-')) {
      const content = trimmed.substring(1).trim();
      // Parse bold within list items
      const parts = content.split(/(\*\*.*?\*\*)/g);
      const rendered = parts.map((part, idx) => {
        if (part.startsWith('**') && part.endsWith('**')) {
          return <strong key={idx} className="text-slate-900 dark:text-white">{part.slice(2, -2)}</strong>;
        }
        return part;
      });
      currentList.push(rendered);
      return;
    }

    // Handle regular paragraphs
    flushList();
    if (trimmed) {
      // Parse bold text in paragraphs
      const parts = trimmed.split(/(\*\*.*?\*\*)/g);
      const rendered = parts.map((part, idx) => {
        if (part.startsWith('**') && part.endsWith('**')) {
          return <strong key={idx} className="text-slate-900 dark:text-white">{part.slice(2, -2)}</strong>;
        }
        return part;
      });

      elements.push(
        <p key={`p-${i}`} className="text-sm leading-relaxed mb-3 text-slate-600 dark:text-slate-400">
          {rendered}
        </p>
      );
    }
  });

  flushList(); // Flush any remaining list items

  return <div className="space-y-1">{elements}</div>;
};

const SharedNoteView = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [activeTab, setActiveTab] = useState('summary');
  const [searchQuery, setSearchQuery] = useState('');

  const videoRef = useRef(null);

  useEffect(() => {
    const pathParts = window.location.pathname.split('/');
    const shareId = pathParts[pathParts.length - 1];

    if (!shareId || shareId === 'shared') { setError(true); setLoading(false); return; }

    // Force Production URL
    const API_BASE_URL = (process.env.REACT_APP_API_URL || "").replace("http://", "https://");

    fetch(`${API_BASE_URL}/api/notes/shared/${shareId}`)
      .then(res => {
        if (!res.ok) throw new Error("Failed");
        return res.json();
      })
      .then(fetchedData => {
        setData(fetchedData);
        setLoading(false);
      })
      .catch((err) => {
        console.error(err);
        setError(true);
        setLoading(false);
      });
  }, []);

  const handleJumpTo = (timestampMs) => {
    if (!data?.video_url) { alert("No video available."); return; }

    setActiveTab('video');
    setTimeout(() => {
      if (videoRef.current) {
        const sec = timestampMs / 1000;
        videoRef.current.currentTime = Math.max(0, sec);
        videoRef.current.play().catch(() => { });
      }
    }, 300);
  };

  const processedEvents = useMemo(() => parseTranscriptData(data?.events), [data?.events]);
  const filteredEvents = useMemo(() => {
    if (!searchQuery) return processedEvents;
    return processedEvents.filter(e => e.displayText.toLowerCase().includes(searchQuery.toLowerCase()));
  }, [processedEvents, searchQuery]);

  if (loading) return <div className="min-h-screen flex items-center justify-center bg-slate-50 dark:bg-[#0F172A]"><Loader2 className="animate-spin text-orange-500 w-8 h-8" /></div>;
  if (error) return <div className="min-h-screen flex flex-col items-center justify-center text-center p-4 bg-slate-50 dark:bg-[#0F172A] text-slate-900 dark:text-white"><AlertCircle size={48} className="text-red-500 mb-4" /><h2>Note not found</h2></div>;

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-[#0F172A] flex flex-col font-sans text-slate-900 dark:text-white">

      {/* HEADER */}
      <div className="bg-white dark:bg-[#1F2833] border-b border-gray-200 dark:border-white/10 px-4 md:px-6 py-4 flex justify-between items-center sticky top-0 z-50 shadow-sm">
        <div className="flex items-center gap-4">
          <img src="/hicapy.png" alt="Logo" className="w-8 h-8 rounded" onError={(e) => e.target.style.display = 'none'} />
          <div>
            <h1 className="text-lg font-bold leading-tight truncate max-w-[200px] md:max-w-md">{data.title}</h1>
            <div className="flex items-center gap-3 text-xs text-slate-500 dark:text-slate-400 mt-1">
              <span className="flex items-center gap-1"><Calendar size={12} /> {new Date(data.date).toLocaleDateString()}</span>
              {data.bot_name && <span className="bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400 px-2 py-0.5 rounded-full font-bold text-[10px]">{data.bot_name}</span>}
            </div>
          </div>
        </div>
        <a href="/" className="hidden md:block px-4 py-2 bg-orange-500 hover:bg-orange-600 text-white text-xs font-bold rounded-full transition-colors">
          Try HiCapy Free
        </a>
      </div>

      {/* MAIN CONTAINER */}
      <div className="max-w-4xl mx-auto w-full p-4 md:p-8 flex-1 flex flex-col">

        {/* TABS HEADER */}
        <div className="flex p-1 bg-white dark:bg-[#1a202c] rounded-xl shadow-sm border border-gray-200 dark:border-white/5 mb-6">
          {['summary', 'transcript', 'video'].map((tab) => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              className={`flex-1 flex items-center justify-center gap-2 py-2.5 text-sm font-bold rounded-lg transition-all ${activeTab === tab
                ? 'bg-orange-500 text-white shadow-md'
                : 'text-slate-500 dark:text-slate-400 hover:bg-slate-50 dark:hover:bg-white/5'
                }`}
            >
              {tab === 'summary' && <FileText size={16} />}
              {tab === 'transcript' && <MessageSquare size={16} />}
              {tab === 'video' && <Video size={16} />}
              <span className="capitalize">{tab === 'summary' ? 'Summary & Actions' : tab}</span>
            </button>
          ))}
        </div>

        {/* TAB CONTENT */}
        <div className="bg-white dark:bg-[#151b23] rounded-2xl shadow-sm border border-gray-200 dark:border-white/10 flex-1 overflow-hidden flex flex-col">

          {/* 1. SUMMARY TAB */}
          {activeTab === 'summary' && (
            <div className="p-6 md:p-8 overflow-y-auto animate-in fade-in slide-in-from-bottom-2 duration-300">
              <div className="mb-10">
                <div className="flex items-center gap-2 mb-4 pb-2 border-b border-gray-100 dark:border-white/10">
                  <div className="p-1.5 bg-blue-100 dark:bg-blue-900/30 rounded-lg text-blue-600 dark:text-blue-400"><FileText size={18} /></div>
                  <h2 className="text-lg font-bold">Executive Summary</h2>
                </div>
                <div className="prose dark:prose-invert max-w-none">
                  {formatContent(data.summary)}
                </div>
              </div>
              {data.todos && data.todos.length > 0 && (
                <div>
                  <div className="flex items-center gap-2 mb-4 pb-2 border-b border-gray-100 dark:border-white/10">
                    <div className="p-1.5 bg-orange-100 dark:bg-orange-900/30 rounded-lg text-orange-600 dark:text-orange-400"><CheckCircle2 size={18} /></div>
                    <h2 className="text-lg font-bold">Action Items</h2>
                  </div>
                  <div className="grid gap-3">
                    {data.todos.map((todo, i) => (
                      <div key={i} className="flex gap-3 p-4 rounded-xl bg-slate-50 dark:bg-white/5 border border-slate-100 dark:border-white/5">
                        <div className="mt-1 w-5 h-5 rounded-full border-2 border-orange-400 dark:border-orange-500 flex items-center justify-center shrink-0">
                          <div className="w-2.5 h-2.5 rounded-full bg-orange-400 dark:bg-orange-500 opacity-0 hover:opacity-100 transition-opacity cursor-pointer" />
                        </div>
                        <p className="text-sm text-slate-800 dark:text-slate-200 font-medium leading-relaxed">
                          {typeof todo === 'string' ? todo : todo.task}
                        </p>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* 2. TRANSCRIPT TAB */}
          {activeTab === 'transcript' && (
            <div className="flex flex-col h-full animate-in fade-in slide-in-from-bottom-2 duration-300">
              <div className="p-4 border-b border-gray-200 dark:border-white/10 bg-slate-50/50 dark:bg-white/5">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={16} />
                  <input
                    type="text"
                    placeholder="Search transcript..."
                    className="w-full pl-10 pr-4 py-2.5 rounded-xl border border-gray-200 dark:border-white/10 bg-white dark:bg-black/20 outline-none focus:ring-2 focus:ring-orange-500/50 text-sm"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                  />
                </div>
              </div>
              <div className="flex-1 overflow-y-auto p-4 space-y-3 custom-scrollbar">
                {filteredEvents.length > 0 ? filteredEvents.map((evt, i) => (
                  <div
                    key={i}
                    onClick={() => handleJumpTo(evt.start || evt.start_ms)}
                    className="group p-3 rounded-xl hover:bg-slate-50 dark:hover:bg-white/5 cursor-pointer border border-transparent hover:border-slate-200 dark:hover:border-white/10 transition-all flex gap-4"
                  >
                    <div className={`w-8 h-8 rounded-lg flex items-center justify-center text-xs font-bold shrink-0 ${evt.displaySpeaker === 'AI' ? 'bg-orange-100 text-orange-700 dark:bg-orange-500/20 dark:text-orange-400' : 'bg-blue-100 text-blue-700 dark:bg-blue-500/20 dark:text-blue-400'}`}>
                      {evt.displaySpeaker?.[0]?.toUpperCase() || "?"}
                    </div>
                    <div>
                      <div className="flex items-center gap-2 mb-1">
                        <span className="text-xs font-bold text-slate-900 dark:text-white">{evt.displaySpeaker}</span>
                        <span className="text-[10px] text-slate-400 font-mono flex items-center gap-1 group-hover:text-orange-500 transition-colors">
                          {new Date(evt.start || evt.start_ms || 0).toISOString().substr(14, 5)}
                          <PlayCircle size={10} className="opacity-0 group-hover:opacity-100" />
                        </span>
                      </div>
                      <p className="text-sm text-slate-600 dark:text-slate-300 leading-relaxed whitespace-pre-wrap">
                        {evt.displayText}
                      </p>
                    </div>
                  </div>
                )) : (
                  <div className="text-center p-8 text-slate-400 text-sm">No transcript found matching "{searchQuery}"</div>
                )}
              </div>
            </div>
          )}

          {/* 3. VIDEO TAB */}
          {activeTab === 'video' && (
            <div className={`flex flex-col h-full animate-in fade-in zoom-in duration-300 relative group justify-center ${data.video_url ? 'bg-black' : 'bg-gray-50 dark:bg-slate-900'}`}>
              {data.video_url ? (
                <>
                  <video
                    ref={videoRef}
                    src={data.video_url}
                    controls
                    className="w-full h-full max-h-[70vh] object-contain"
                  />
                  <div className="absolute top-4 left-1/2 -translate-x-1/2 bg-black/60 backdrop-blur-md px-4 py-2 rounded-full text-xs text-white opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none">
                    Jumped to timestamp
                  </div>
                </>
              ) : (
                <div className="flex flex-col items-center justify-center h-64 text-slate-500 dark:text-slate-400">
                  <Video size={48} className="mb-4 opacity-50" />
                  <p>No video recording available.</p>
                </div>
              )}
            </div>
          )}

        </div>
      </div>
    </div>
  );
};

export default SharedNoteView;